import { TRACKING_ACTIONS, trackEvent } from '@medflex/utils/Tracking';
import PropTypes from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../Button';
import { IconListAdd } from '../Icons';
import PopupMenu from '../PopupMenu';

export const TextSnippetPopupKinds = {
  TEXT: 'text',
  ICON: 'icon',
  TEXT_ICON: 'text_icon',
};

const TextSnippetsPopup = ({ snippets, onClick, disabled, kind, buttonClassName }) => {
  const { t } = useTranslation('components');

  const handleToggle = event => {
    trackEvent(event, { action: TRACKING_ACTIONS.OPEN, actionText: 'insertTextBlock' });
  };

  const handleClick = (event, snippet) => {
    trackEvent(event, { actionText: 'insertTextBlock' });
    onClick(snippet);
  };

  return (
    <PopupMenu>
      <PopupMenu.Menu>
        <PopupMenu.Header className="text-blue-500 px-6 py-4 lg:max-w-96">
          {() => <span className="text-slate-700">{t('snippets_popup.heading')}</span>}
        </PopupMenu.Header>
        <PopupMenu.Group className="lg:max-w-96 max-h-64 overflow-y-auto">
          {snippets.map(snippet => (
            <PopupMenu.Item
              key={snippet.id}
              className="border-b"
              onClick={event => handleClick(event, snippet)}
            >
              <span className="text-sm font-semibold text-slate-600 mr-1 truncate flex-grow flex-shrink-0 max-w-three-quarters">
                {snippet.title}
              </span>
              <span className="flex-grow" />
              <span className="text-sm font-normal text-slate-600 truncate">{snippet.text}</span>
            </PopupMenu.Item>
          ))}
        </PopupMenu.Group>
        {snippets.length === 0 && (
          <PopupMenu.Item className="italic text-slate-600 pointer-events-none lg:max-w-96">
            <div className="pt-4 pb-12">{t('snippets_popup.empty')}</div>
          </PopupMenu.Item>
        )}
      </PopupMenu.Menu>
      <PopupMenu.Trigger>
        {({ triggerRef }) => (
          <Button
            innerRef={triggerRef}
            onClick={handleToggle}
            shape={kind === TextSnippetPopupKinds.ICON ? 'square' : 'default'}
            kind="secondary:ghost"
            isDisabled={disabled}
            className={`${kind === TextSnippetPopupKinds.ICON ? '-scale-x-1' : ''} ${buttonClassName}`}
          >
            {[TextSnippetPopupKinds.ICON, TextSnippetPopupKinds.TEXT_ICON].includes(kind) && (
              <IconListAdd />
            )}
            {kind !== TextSnippetPopupKinds.ICON && <p>{t('snippets_popup.button')}</p>}
          </Button>
        )}
      </PopupMenu.Trigger>
    </PopupMenu>
  );
};

TextSnippetsPopup.defaultProps = {
  snippets: [],
  onClick: () => {},
  disabled: false,
  kind: TextSnippetPopupKinds.ICON,
  buttonClassName: '',
};

TextSnippetsPopup.propTypes = {
  snippets: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      text: PropTypes.string,
      id: PropTypes.string,
    }),
  ),
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  kind: PropTypes.oneOf(Object.values(TextSnippetPopupKinds)),
  buttonClassName: PropTypes.string,
};

export default TextSnippetsPopup;
